import { useCallback, useEffect, useRef } from "react";
import { useTranslation as useI18nTranslation } from "react-i18next";
import type { i18n as I18nInstance } from "i18next";

/**
 * localStorage key the i18next browser language detector persists the
 * visitor's language under (see src/i18n/config.ts `detection.caches`).
 */
export const PERSISTED_LANGUAGE_KEY = "i18nextLng";

const readPersistedLanguage = (): string | null => {
  try {
    return window.localStorage.getItem(PERSISTED_LANGUAGE_KEY);
  } catch {
    return null;
  }
};

const writePersistedLanguage = (value: string | null): void => {
  try {
    if (value === null) {
      window.localStorage.removeItem(PERSISTED_LANGUAGE_KEY);
    } else {
      window.localStorage.setItem(PERSISTED_LANGUAGE_KEY, value);
    }
  } catch {
    // Storage unavailable (private mode / quota) — nothing to protect.
  }
};

/**
 * Switch the running i18n instance to `lng` for the current page only.
 *
 * i18next's `changeLanguage` also writes the language into localStorage via
 * the language detector cache, which is what turned "open a RO invitation"
 * into "the whole app is now RO" for an organizer browsing in EN (JUB-81).
 * The detector caches synchronously inside `changeLanguage`, so the persisted
 * value is snapshotted before the call and written back straight after; the
 * returned promise still resolves once the language is applied.
 */
export function applyPageLanguage(i18n: I18nInstance, lng: string): Promise<unknown> {
  const persisted = readPersistedLanguage();
  const changed = i18n.changeLanguage(lng);
  writePersistedLanguage(persisted);
  return changed;
}

/**
 * Page-scoped language for the public event page (JUB-81).
 *
 * The event's default language must drive everything rendered while the
 * page is mounted (templates, toasts, cookie banner — JUB-63), so the shared
 * i18n instance is switched, but:
 *  - the visitor's persisted preference (`i18nextLng`) is left untouched, and
 *  - the language active when the page mounted is restored on unmount, so an
 *    in-app navigation back to /ro/* or /en/* never inherits the event's language.
 *
 * Returns a stable `setPageLanguage(lng)` callback.
 */
export function usePageScopedLanguage(): (lng: string) => Promise<unknown> {
  const { i18n } = useI18nTranslation();
  const languageOnMountRef = useRef<string>(i18n.language);

  useEffect(() => {
    const previous = languageOnMountRef.current;
    return () => {
      if (previous && i18n.language !== previous) {
        void applyPageLanguage(i18n, previous);
      }
    };
  }, [i18n]);

  return useCallback((lng: string) => applyPageLanguage(i18n, lng), [i18n]);
}
